import { componentModelMetadataKey } from 'decorators/componentModel';
import { wccModules } from 'enums/wccModules';
import { isDisposable } from 'interfaces/disposable';
import { isInitializable } from 'interfaces/initializable';
import { isInjectable } from 'interfaces/injectable';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { ServicesContext } from 'services/context';
import { createExtendedContainer } from '../../../configuration/inversify/container/extended';
import { EffectsContainer, withEffects } from '../../../mixins/withEffects';
import { isLazyComponent } from '../lazyComponent';

const BaseViewModel = require('viewmodels/base_es5');

const maxLoadAttempts = 1000;

function createViewModel(ViewModel: any, model: any, data: any, componentInfo: ko.components.ComponentInfo) {
    const ctx = new ServicesContext();
    const viewmodel = new ViewModel(model, componentInfo, ctx, data, {});

    ko.utils.domNodeDisposal.addDisposeCallback(componentInfo.element, () => ctx.dispose());

    return viewmodel;
}

function createViewModelv2(ViewModel: any, model: any, data: any, componentInfo: ko.components.ComponentInfo) {
    return new ViewModel({
        model: model,
        data: data,
        element: componentInfo.element,
        templateNodes: componentInfo.templateNodes
    });
}

function createViewModelv3(ViewModel: any, model: any, data: any, componentInfo: ko.components.ComponentInfo) {
    type Model = typeof model;
    type Data = typeof data;

    const [container, disposeContainer] = createExtendedContainer(module => module instanceof ViewModel);

    container.bind<Model>(wccModules.componentConfig).toConstantValue(model);
    container.bind<Data>(wccModules.componentData).toConstantValue(data);
    container.bind<ko.components.ComponentInfo>(wccModules.componentInfo).toConstantValue(componentInfo);
    container.bind(ViewModel).toSelf();

    const viewmodel = container.get(ViewModel);

    ko.utils.domNodeDisposal.addDisposeCallback(componentInfo.element, () => disposeContainer());

    return viewmodel;
}

function initViewModel(viewmodel: unknown, element: Node, attempt = 1) {
    return new Promise<boolean>((resolve, reject) => {
        if (document.body.contains(element)) {
            if (isInitializable(viewmodel))
                viewmodel.init();

            if (isLazyComponent(viewmodel)) {
                ko.applyBindingsToNode(element, {
                    'attr': {
                        'component-ready': viewmodel.isComponentReady
                    }
                }, {});
            } else {
                $(element).attr('component-ready', 'true');
            }

            resolve(true);
        } else if (attempt <= maxLoadAttempts) {
            requestAnimationFrame(() => resolve(initViewModel(viewmodel, element, attempt + 1)));
        } else {
            if (isDisposable(viewmodel))
                viewmodel.dispose();

            resolve(false);
        }
    });
}

function exposeViewModel(ref: Subscribable<any>, viewmodel: any, effects: EffectsContainer, idx: SubscribableOrNullableValue<number>) {
    if (ko.isObservableArray(ref)) {
        effects.register({ dispose: () => ref.remove(viewmodel) });

        effects.register(idx => {
            if (idx != undefined) {
                if (ref().indexOf(viewmodel) != idx)
                    ref.remove(viewmodel);

                ref.splice(idx, 0, viewmodel);
            } else if (!ref().includes(viewmodel)) {
                ref.push(viewmodel);
            }
        }, [idx], false);
    } else if (ko.isWritableObservable(ref)) {
        ref(viewmodel);
        effects.register({ dispose: () => ref(undefined) });
    }
}

export class ViewModelFactory {
    constructor(private ViewModel: any) { }

    createViewModel(params: ko.components.ViewModelParams, componentInfo: ko.components.ComponentInfo) {
        const effects = withEffects();
        const element = componentInfo.element;

        if (params.model === undefined)
            params = { model: params };

        let { model, data = {}, ref, idx } = params;

        model = ko.unwrap(model);
        model = model != null ? model : {};

        data = ko.unwrap(data);
        data = data != null ? data : {};

        let Model = this.ViewModel.Model ?? Reflect.getMetadata(componentModelMetadataKey, this.ViewModel);

        if (Model != undefined)
            model = model instanceof Model ? model : new Model(model, data);

        let viewmodel: any;

        if (this.ViewModel.prototype instanceof BaseViewModel) {
            viewmodel = createViewModelv2(this.ViewModel, model, data, componentInfo);
        } else if (isInjectable(this.ViewModel)) {
            viewmodel = createViewModelv3(this.ViewModel, model, data, componentInfo);
        } else {
            viewmodel = createViewModel(this.ViewModel, model, data, componentInfo)
        }        

        if (viewmodel.beforeInit)
            viewmodel.beforeInit();

        if (ref != undefined)
            exposeViewModel(ref, viewmodel, effects, idx);

        initViewModel(viewmodel, element);

        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
            effects.dispose();
            
            if (viewmodel.dispose) {
                viewmodel.dispose();
            }
        });

        return viewmodel;
    }
}